import { Environment } from "../utils/Environment";
import React, { useState, useEffect } from "react";
import { postWithUserTableParams } from "../services/fetchWrapper";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import InfoIcon from "@mui/icons-material/Info";
import { useAuth } from "../utils/AuthContext";
import Layout from "./Layout";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";


const apiUrl = Environment.getApiUrl();

interface ProcessConcreteSteelRequest {
    job_id: string;
    main_table_name: string;
    layer_name?: string;
    compare_table_name: string;
    material_col: string;
    material_list: string[];
    score_column: string;
    geom_column: string;
    compare_geom_col: string;
    grid_col: string;
    grid_score: number[];
    score: number[];
    client_id: string;
    flow_name: string;
}

const ProcessConcreteOrSteel: React.FC = () => {
    const { isLoggedIn, userName, logout } = useAuth();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [baseTableName, setBaseTableName] = useState<string>("");
    const [concreteTableName, setConcreteTableName] = useState<string>("");
    const [steelTableName, setSteelTableName] = useState<string>("");

    const [possibleBaseColumns, setPossibleBaseColumns] = useState<string[]>([]);
    const [possibleConcreteColumns, setPossibleConcreteColumns] = useState<string[]>([]);
    const [possibleSteelColumns, setPossibleSteelColumns] = useState<string[]>([]);

    const [baseGeometryColumn, setBaseGeometryColumn] = useState<string>("");
    const [baseMaterialColumn, setBaseMaterialColumn] = useState<string>("");
    const [baseMaterialValues, setBaseMaterialValues] = useState<string[]>([]);

    const [concreteGeometryColumn, setConcreteGeometryColumn] = useState<string>("");
    const [concreteGridcodeColumn, setConcreteGridcodeColumn] = useState<string>("");
    const [concreteGridcodeValues, setConcreteGridcodeValues] = useState<string[]>([]);

    const [steelGeometryColumn, setSteelGeometryColumn] = useState<string>("");
    const [steelGridcodeColumn, setSteelGridcodeColumn] = useState<string>("");
    const [steelGridcodeValues, setSteelGridcodeValues] = useState<string[]>([]);

    const [readyToProcess, setReadyToProcess] = useState(false);

    const [uploadStatus, setUploadStatus] = useState<string | null>(null);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [pollingJobIds, setPollingJobIds] = useState<string[]>([]);
    const [batchPollingActive, setBatchPollingActive] = useState(false);
    const [jobStatusMap, setJobStatusMap] = useState<Record<string, string>>({});
    const [dialogOpen, setDialogOpen] = useState(false);
    const [outputPath, setOutputPath] = useState("output.gpkg");

    useEffect(() => {
        if (uploadStatus) setDialogOpen(true);
    }, [uploadStatus]);

    useEffect(() => {
        if (!batchPollingActive || pollingJobIds.length === 0) return;

        let incompleteIds = pollingJobIds.filter(
            id => !["completed", "success", "failed", "unknown", "error"].includes(jobStatusMap[id] ?? "")
        );
        incompleteIds = incompleteIds.sort((a, b) => Number(a) - Number(b));

        if (incompleteIds.length === 0) {
            setUploadStatus("completed");
            setStatusMessage("All jobs complete.");
            setPollingJobIds([]);
            setBatchPollingActive(false);
            return;
        }

        const idToPoll = incompleteIds[0];
        const timer = setTimeout(async () => {
            try {
                const response = await fetch(`${apiUrl}/upload-status/${idToPoll}`);
                if (response.ok) {
                    const data = await response.json();
                    setJobStatusMap(prev => ({
                        ...prev,
                        [idToPoll]: data.upload_status
                    }));
                    setUploadStatus(data.upload_status);
                    setStatusMessage(data.message);
                } else {
                    setStatusMessage("Failed to get upload status.");
                }
            } catch (err) {
                setStatusMessage("Error polling job status.");
            }
        }, 10000);

        return () => clearTimeout(timer);
    }, [batchPollingActive, pollingJobIds, jobStatusMap, apiUrl]);

    const buildRequest = (
        flow: "Concrete" | "Steel" | "Base"
    ): ProcessConcreteSteelRequest | null => {
        const client_id = localStorage.getItem("client_id") || "";
        const job_id = localStorage.getItem("job_id") || "";
        if (flow === "Concrete") {
            if (
                !concreteGeometryColumn ||
                !concreteGridcodeColumn ||
                !concreteGridcodeValues.length
            )
                return null;
            return {
                job_id,
                main_table_name: baseTableName,
                layer_name: undefined,
                compare_table_name: concreteTableName,
                material_col: baseMaterialColumn,
                material_list: baseMaterialValues,
                score_column: "lof_cncrte",
                geom_column: baseGeometryColumn,
                compare_geom_col: concreteGeometryColumn,
                grid_col: concreteGridcodeColumn,
                grid_score: concreteGridcodeValues.map(Number).filter(n => !isNaN(n)),
                score: [1, 3, 5], // fill if needed
                client_id,
                flow_name: "Concrete",
            };
        }
        if (flow === "Steel") {
            if (
                !steelGeometryColumn ||
                !steelGridcodeColumn ||
                !steelGridcodeValues.length
            )
                return null;
            return {
                job_id,
                main_table_name: baseTableName,
                layer_name: undefined,
                compare_table_name: steelTableName,
                material_col: baseMaterialColumn,
                material_list: baseMaterialValues,
                score_column: "lof_steel",
                geom_column: baseGeometryColumn,
                compare_geom_col: steelGeometryColumn,
                grid_col: steelGridcodeColumn,
                grid_score: steelGridcodeValues.map(Number).filter(n => !isNaN(n)),
                score: [1, 3, 5],
                client_id,
                flow_name: "Steel",
            };
        }
        // Optionally handle "Base" flow if needed
        return null;
    };


    // Fetch possible columns (from backend)
    useEffect(() => {
        const fetchColumns = async () => {
            const username = localStorage.getItem("userName") || "";
            const client_id = localStorage.getItem("client_id") || "";
            const job_id = localStorage.getItem("job_id") || "";
            try {
                const response = await postWithUserTableParams(`${apiUrl}/get-concretesteel-columns`, {
                    username,
                    client_id,
                    job_id,
                });
                if (!response.ok) return;
                const data = await response.json();
                if (data.possible_material_columns) {
                    const baseCols = (data.possible_material_columns.base_data || []).map(
                        (col: any) => typeof col === "string" ? col : col.column_name
                    );
                    const concreteCols = (data.possible_material_columns.concrete || []).map(
                        (col: any) => typeof col === "string" ? col : col.column_name
                    );
                    const steelCols = (data.possible_material_columns.steel || []).map(
                        (col: any) => typeof col === "string" ? col : col.column_name
                    );

                    setPossibleBaseColumns(baseCols);
                    setPossibleConcreteColumns(concreteCols);
                    setPossibleSteelColumns(steelCols);

                    // Try to auto-select geometry and material columns
                    setBaseGeometryColumn(
                        baseCols.find((col: string) => /geom|geometry/i.test(col)) || ""
                    );
                    setBaseMaterialColumn(
                        baseCols.find((col: string) => /material|d_pipetype|pipe/i.test(col)) || ""
                    );
                    setConcreteGeometryColumn(
                        concreteCols.find((col: string) => /geom|geometry/i.test(col)) || ""
                    );
                    setConcreteGridcodeColumn(
                        concreteCols.find((col: string) => /gridcode|grid|corrosivit|corrosive|corrosivity/i.test(col)) || ""
                    );
                    setSteelGeometryColumn(
                        steelCols.find((col: string) => /geom|geometry/i.test(col)) || ""
                    );
                    setSteelGridcodeColumn(
                        steelCols.find((col: string) => /gridcode|grid|corrosivit|corrosive|corrosivity/i.test(col)) || ""
                    );
                }
                if (data.possible_material_columns.concrete.length === 0 && data.possible_material_columns.steel.length === 0) {
                    setError("No concrete or steel data found. Please load the relevant datasets before processing.");
                }
            } catch { }
        };
        fetchColumns();
    }, [apiUrl]);


    // Fetch concrete/steel JSONs
    // useEffect(() => {
    //     const fetchClientData = async () => {
    //         setLoading(true);
    //         setError(null);
    //         try {
    //             const response = await postWithUserTableParams(`${apiUrl}/get-client-data-for-concretesteel-scoring`, {
    //                 username,
    //                 client_id,
    //                 job_id,
    //             });
    //             if (!response.ok) {
    //                 throw new Error("Failed to fetch client data");
    //             }
    //             const data = await response.json();
    //             if (data.status !== 'success' || !Array.isArray(data.jsons)) {
    //                 throw new Error("Invalid response format from backend");
    //             }
    //             setConcreteSteelJsons(data.jsons);
    //         } catch (err: any) {
    //             setError(err.message || "Failed to fetch client data");
    //         } finally {
    //             setLoading(false);
    //         }
    //     };
    //     fetchClientData();
    // }, [apiUrl, username, client_id, job_id]);

    const handleFetchAllColumnValues = async () => {
        setLoading(true);
        setError(null);
        try {
            await handleFetchColumnValues(concreteGridcodeColumn, "concrete", setConcreteGridcodeValues, setConcreteTableName);
            await handleFetchColumnValues(steelGridcodeColumn, "steel", setSteelGridcodeValues, setSteelTableName);
            await handleFetchColumnValues(baseMaterialColumn, "base", setBaseMaterialValues, setBaseTableName);
            setReadyToProcess(true);
        } catch (err: any) {
            setError(err.message || "Failed to fetch column values");
        } finally {
            setLoading(false);
        }
    };

    const handleFetchColumnValues = async (
        columnName: string,
        tableType: "concrete" | "steel" | "base",
        setValues: (values: string[]) => void,
        setTableName?: (name: string) => void
    ) => {
        const username = localStorage.getItem("userName") || "";
        const client_id = localStorage.getItem("client_id") || "";
        const job_id = localStorage.getItem("job_id") || "";
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${apiUrl}/get-unique-values-for-column`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    client_id,
                    job_id,
                    column_name: columnName,
                    table_type: tableType,
                    username
                }),
            });
            if (!response.ok) throw new Error("Failed to fetch column values");
            const data = await response.json();
            setValues(
                Array.isArray(data.column_values)
                    ? data.column_values.filter((v: unknown) => v !== null && v !== undefined && (typeof v === "string" ? v.trim() : true))
                    : []
            );
            if (setTableName && data.table_name) setTableName(data.table_name);
        } catch (err: any) {
            setError(err.message || "Failed to fetch column values. Please ensure Base Dataset is loaded.");
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadGeopackage = async () => {
        const username = localStorage.getItem("userName") || "";
        const client_id = localStorage.getItem("client_id") || "";
        const job_id = localStorage.getItem("job_id") || "";
        try {
            setLoading(true);
            setError(null);
            // Ensure .gpkg extension
            let filename = outputPath.trim();
            if (!filename.toLowerCase().endsWith(".gpkg")) {
                filename += ".gpkg";
            }
            const response = await fetch(`${apiUrl}/download-geopackage`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    username,
                    client_id,
                    job_id,
                    package_save_path: filename
                }),
            });
            if (!response.ok) {
                throw new Error("Failed to download geopackage");
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            setError(err.message || "Failed to download geopackage");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveUserSelectedColumns = async () => {
        const client_id = localStorage.getItem("client_id") || "";
        const job_id = localStorage.getItem("job_id") || "";
        setLoading(true);
        setError(null);

        try {
            // Save customer calc columns first
            const columnsToSave = [
                {
                    client_id,
                    job_id,
                    column_name: baseGeometryColumn,
                    column_value: "",
                    flow_name: "geometry",
                    description: "base dataset column defining geometry type/shape data in gis file"
                },
                {
                    client_id,
                    job_id,
                    column_name: baseMaterialColumn,
                    column_value: baseMaterialValues.join(","),
                    flow_name: "base_material",
                    description: "base dataset material column and its values"
                },
                {
                    client_id,
                    job_id,
                    column_name: concreteGeometryColumn,
                    column_value: "",
                    flow_name: "concrete",
                    description: "comparison dataset column defining geometry type/shape data in gis file"
                },
                {
                    client_id,
                    job_id,
                    column_name: concreteGridcodeColumn,
                    column_value: concreteGridcodeValues.join(","),
                    flow_name: "concrete",
                    description: "grid code for concrete"
                },
                {
                    client_id,
                    job_id,
                    column_name: steelGeometryColumn,
                    column_value: "",
                    flow_name: "steel",
                    description: "comparison dataset column defining geometry type/shape data in gis file"
                },
                {
                    client_id,
                    job_id,
                    column_name: steelGridcodeColumn,
                    column_value: steelGridcodeValues.join(","),
                    flow_name: "steel",
                    description: "grid code for steel"
                }
            ];
            const saveResponse = await fetch(`${apiUrl}/save-customer-calc-columns`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ columns: columnsToSave, client_id }),
            });
            if (!saveResponse.ok) {
                throw new Error("Failed to save customer calc columns");
            }
        } catch (err: any) {
            setError(err.message || "Failed to save customer calc columns");
            throw err; // rethrow so the main handler can catch it
        }
    };

    const handleProcessConcreteSteel = async () => {
        setLoading(true);
        setError(null);
        setUploadStatus(null);
        setStatusMessage(null);
        setPollingJobIds([]);
        setBatchPollingActive(false);
        setJobStatusMap({});
        try {
            await handleSaveUserSelectedColumns();

            // Build requests from user selections
            const requests: ProcessConcreteSteelRequest[] = [];
            const concreteReq = buildRequest("Concrete");
            if (concreteReq) requests.push(concreteReq);
            const steelReq = buildRequest("Steel");
            if (steelReq) requests.push(steelReq);

            if (!requests.length) {
                throw new Error("No valid requests to process. Please check your selections.");
            }

            const response = await fetch(`${apiUrl}/get-concrete-and-steel-scores`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(requests),
            });
            if (!response.ok) {
                throw new Error("Failed to process one or more concrete/steel scores");
            }
            const data = await response.json();
            if (data.status === "processing" && Array.isArray(data.ids) && data.ids.length > 0) {
                setPollingJobIds(data.ids);
                setBatchPollingActive(true);
                setUploadStatus("processing");
                setStatusMessage(data.message || "Batch started...");
            } else if (data.status === "success") {
                setUploadStatus("finishing up");
                setStatusMessage(data.message || "Processing complete.");
            } else {
                setUploadStatus("error");
                setStatusMessage(data.message || "Error processing.");
            }
        } catch (err: any) {
            setError(err.message || "Failed to process concrete or steel scores");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout isLoggedIn={isLoggedIn} onLogout={logout} userName={userName}>
            <Box sx={{ maxWidth: 600, mx: "auto", mt: 4 }}>
                <Typography variant="h5" sx={{ mb: 2 }}>Process Concrete and Steel Scores</Typography>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <InfoIcon color="info" sx={{ mr: 1 }} />
                    <Typography variant="body2" color="info.main">
                        Select correct values from the dropdown. If they are not present, manually insert the correct column.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={
                        loading ||
                        !baseGeometryColumn ||
                        !baseMaterialColumn ||
                        !concreteGeometryColumn ||
                        !concreteGridcodeColumn ||
                        !steelGeometryColumn ||
                        !steelGridcodeColumn
                    }
                    onClick={handleFetchAllColumnValues}
                    sx={{ my: 3 }}
                >
                    {loading ? "Loading values..." : "Fetch & Review Values"}
                </Button>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6">Base Dataset</Typography>
                    <Autocomplete
                        freeSolo
                        options={possibleBaseColumns}
                        value={baseGeometryColumn}
                        onChange={(_, v) => setBaseGeometryColumn(v || "")}
                        renderInput={params => <TextField {...params} label="Base Geometry Column" variant="outlined" />}
                        sx={{ mb: 2 }}
                    />
                    <Autocomplete
                        freeSolo
                        options={possibleBaseColumns}
                        value={baseMaterialColumn}
                        onChange={(_, v) => setBaseMaterialColumn(v || "")}
                        renderInput={params => <TextField {...params} label="Base Material Column (containig material names ie: iron)" variant="outlined" />}
                        sx={{ mb: 2 }}
                    />
                    {baseMaterialColumn && (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                You do not need to input anything in "Base Material Values". Values will appear automatically once you have selected the geometry and gridcode columns and clicked "Fetch & Review Values".
                            </Typography>
                            <Autocomplete
                                multiple
                                freeSolo
                                options={baseMaterialValues}
                                value={baseMaterialValues}
                                onChange={(_, v) => setBaseMaterialValues(v as string[])}
                                renderInput={params => <TextField {...params} label="Base Material Values" variant="outlined" />}
                                sx={{ mb: 2 }}
                            />
                        </>
                    )}
                </Box>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6">Concrete</Typography>
                    <Autocomplete
                        freeSolo
                        options={possibleConcreteColumns}
                        value={concreteGeometryColumn}
                        onChange={(_, v) => setConcreteGeometryColumn(v || "")}
                        renderInput={params => <TextField {...params} label="Concrete Geometry Column" variant="outlined" />}
                        sx={{ mb: 2 }}
                    />
                    <Autocomplete
                        freeSolo
                        options={possibleConcreteColumns}
                        value={concreteGridcodeColumn}
                        onChange={(_, v) => setConcreteGridcodeColumn(v || "")}
                        renderInput={params => <TextField {...params} label="Concrete Gridcode Column" variant="outlined" />}
                        sx={{ mb: 2 }}
                    />
                    {concreteGridcodeColumn && (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                You do not need to input anything in "Concrete Gridcode Values". Values will appear automatically once you have selected the geometry and gridcode columns and clicked "Fetch & Review Values".
                            </Typography>
                            <Autocomplete
                                multiple
                                freeSolo
                                options={concreteGridcodeValues}
                                value={concreteGridcodeValues}
                                onChange={(_, v) => setConcreteGridcodeValues(v as string[])}
                                renderInput={params => <TextField {...params} label="Concrete Gridcode Values" variant="outlined" />}
                                sx={{ mb: 2 }}
                            />
                        </>
                    )}
                </Box>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6">Steel</Typography>
                    <Autocomplete
                        freeSolo
                        options={possibleSteelColumns}
                        value={steelGeometryColumn}
                        onChange={(_, v) => setSteelGeometryColumn(v || "")}
                        renderInput={params => <TextField {...params} label="Steel Geometry Column" variant="outlined" />}
                        sx={{ mb: 2 }}
                    />
                    <Autocomplete
                        freeSolo
                        options={possibleSteelColumns}
                        value={steelGridcodeColumn}
                        onChange={(_, v) => setSteelGridcodeColumn(v || "")}
                        renderInput={params => <TextField {...params} label="Steel Gridcode Column" variant="outlined" />}
                        sx={{ mb: 2 }}
                    />
                    {steelGridcodeColumn && (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                You do not need to input anything in "Steel Gridcode Values". Values will appear automatically once you have selected the geometry and gridcode columns and clicked "Fetch & Review Values".
                            </Typography>
                            <Autocomplete
                                multiple
                                freeSolo
                                options={steelGridcodeValues}
                                value={steelGridcodeValues}
                                onChange={(_, v) => setSteelGridcodeValues(v as string[])}
                                renderInput={params => <TextField {...params} label="Steel Gridcode Values" variant="outlined" />}
                                sx={{ mb: 2 }}
                            />
                        </>
                    )}
                </Box>
                <Button
                    variant="contained"
                    color="success"
                    disabled={!readyToProcess || loading}
                    onClick={handleProcessConcreteSteel}
                    sx={{ my: 3 }}
                >
                    {loading ? "Processing..." : "Confirm & Process"}
                </Button>
                <div style={{ margin: "16px 0" }}>
                    <TextField
                        label="Output Geopackage Filename"
                        value={outputPath}
                        onChange={e => setOutputPath(e.target.value)}
                        helperText="Name of your downloaded file (e.g., output.gpkg)"
                        sx={{ width: 300 }}
                    />
                </div>
                <Button
                    variant="outlined"
                    color="secondary"
                    disabled={loading || uploadStatus !== "completed"}
                    onClick={handleDownloadGeopackage}
                >
                    Download Geopackage
                </Button>
                <Dialog
                    open={dialogOpen}
                    aria-labelledby="processing-status-title"
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle id="processing-status-title">Processing Status</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2">
                            Status: {uploadStatus}
                        </Typography>
                        <Typography variant="body2">
                            Message: {statusMessage}
                        </Typography>
                        {uploadStatus === "processing" && (
                            <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
                                <CircularProgress size={24} sx={{ mr: 1 }} />
                                <Typography variant="body2">Processing, please wait...</Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: "center" }}>
                        {uploadStatus === "completed" && (
                            <Button
                                onClick={() => setDialogOpen(false)}
                                autoFocus
                                variant="contained"
                            >
                                Okay
                            </Button>
                        )}
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={!!error}
                    onClose={() => setError(null)}
                    aria-labelledby="error-dialog-title"
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle id="error-dialog-title" sx={{ color: "error.main" }}>
                        Error
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" sx={{ fontWeight: "bold", color: "error.main" }}>
                            {error}
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: "center" }}>
                        <Button onClick={() => setError(null)} variant="contained" color="error" autoFocus>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Layout>
    );
}

export default ProcessConcreteOrSteel;